import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  contentChild,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatBadge } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import {
  MatButtonToggle,
  MatButtonToggleChange,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatRipple } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { dsDtos, dsEnums } from '@vectronic/openApi-admin';
import {
  buildMultiDeviceGridColumns,
  DeviceTypeParameterConfigWithAvailableListAndParameter,
  ParameterDataService,
  ParameterFormatService,
  ParameterItem,
  ParameterWithData,
  Translator,
} from '@vectronic/util';
import { ParameterValueComponent } from '../parameter-value/parameter-value.component';

type CategoryTitleValue = dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP' | 'DROP_OFF';

@Component({
  selector: 'vectronic-parameter-view',
  imports: [
    CommonModule,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCardTitle,
    ParameterValueComponent,
    MatRipple,
    MatBadge,
    MatButtonToggle,
    MatButtonToggleGroup,
  ],
  templateUrl: './parameter-view.component.html',
  styleUrl: './parameter-view.component.scss',
})
export class ParameterViewComponent implements OnInit {
  parameterDataService = inject(ParameterDataService);
  private parameterFormatService = inject(ParameterFormatService);

  parameters = input<dsDtos.Parameter[]>();
  categories = input<dsDtos.ParameterCategory[]>();
  subsystems = input<dsDtos.Subsystem[]>();
  isFullScreen = input(false);
  viewType = input<'accordion' | 'tab'>('accordion');
  isEditable = input(false);
  deviceTypeParameterConfigWithAvailableList =
    input<dsDtos.DeviceTypeParameterConfigWithAvailableList[]>();
  collarIds = input<number[]>();
  categoryTitles = input<{ label: string; value: CategoryTitleValue }[]>();
  userAccess = input<dsDtos.DeviceTypeParameterConfig['userAccess'][]>();
  showReset = input<boolean>();
  showSubmit = input<boolean>();
  showReload = input<boolean>();
  showClear = input<boolean>();
  showInfo = input(true);
  showFactory = input(true);
  isBluetoothLost = input(false);
  showEditButton = input<boolean>();
  basicOrAdvanced = input<'basic' | 'advanced'>();
  userOrFactory = input<'customer' | 'allUser' | 'allFactory' | 'all'>();
  height = input<string>();
  presetPatternParameter = input<dsDtos.Parameter>();
  customCategoryCounts = input<{ [key: string]: number }>();
  hideChangedParameterData = input<boolean>();
  shouldInitiallyExpanded = input<boolean>();
  isMaxParametersReached = input<boolean>();
  showBasicOrAdvanced = input<boolean>();
  userSubsystemVisibility = input<boolean[]>();
  showOnlyFactoryLabel = input<boolean>(false);
  isChanged = input<boolean>(false);
  componentId = input<string | number>();
  warningByParameterId = input<Map<string, string>>();
  warningTooltipByParameterId = input<Map<string, string>>();
  errorByParameterId = input<Map<string, string>>();
  valueOverrideByParameterId = input<Map<string, Map<string, string>>>();
  disabledByParameterId = input<Map<string, boolean>>();
  isFilled = input<boolean>();
  isRemote = input<boolean>(true);
  columnWidth = input<string>('180px');
  columnWidthByDeviceCount = input<Partial<Record<number, string>>>();
  differingParameterIds = input<Set<string>>();
  showMode = input<'differences' | 'all'>('all');
  hideScroll = input<boolean>(false);
  hideTitle = input<boolean>(false);

  componentIdSubscription = toObservable(this.componentId)
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      this.init();
    });

  selectedCategoryId = signal<string>(undefined);
  customCategorySelectedValue = signal<CategoryTitleValue>(undefined);
  parametersEditState = signal<{ [key: string]: boolean }>({});

  gpsCategory = contentChild<any>('gpsCategory');
  proximityCategory = contentChild<any>('proximityCategory');
  communicationCategory = contentChild<any>('communicationCategory');
  beaconCategory = contentChild<any>('beaconCategory');
  cameraCategory = contentChild<any>('cameraCategory');
  vfCategory = contentChild<any>('vfCategory');
  solexGpsCategory = contentChild<any>('solexGpsCategory');
  vfMapCategory = contentChild<any>('vfMapCategory');
  dropOffCategory = contentChild<any>('dropOffCategory');
  tabTitlesTop = contentChild<any>('tabTitlesTop');
  tabParametersTop = contentChild<any>('tabParametersTop');

  submitParameter = output<dsDtos.ParameterData[]>();
  reload = output();
  clear = output();
  basicOrAdvancedChange = output<'basic' | 'advanced'>();
  changeCategory = output<string>();

  /** multiple selection for categories*/
  selectedCategories = new SelectionModel<string>(true);

  /** multiple selection for subCategories*/
  selectedSubCategories = new SelectionModel<string>(true);

  columnTitlesFromOverrides = computed<string[]>(() => {
    const map = this.valueOverrideByParameterId();
    if (!map?.size) return [];
    return Array.from(map.keys());
  });

  accordionGridColumns = computed<string>(() => {
    const n = this.columnTitlesFromOverrides().length;
    return buildMultiDeviceGridColumns(n, this.columnWidth(), this.columnWidthByDeviceCount());
  });

  disabledSubsystemIds = computed(() => {
    if (!this.userSubsystemVisibility()) return [];
    const disabledIds = this.findFalseIndexes(this.userSubsystemVisibility());
    return disabledIds?.map((disabledId) => {
      const subsystem = this.subsystems()?.find(({ identifier }) => identifier === disabledId);
      return subsystem?.id;
    });
  });

  distinctFactoryAndUserParameterData = computed(() => {
    const updatedParameterDataList = this.hideChangedParameterData()
      ? this.parameterDataService.parameterDataList()
      : this.parameterDataService.updatedParameterDataList();
    return updatedParameterDataList.filter((updatedParameterData) =>
      updatedParameterDataList.some(
        (p) =>
          p.parameterId === updatedParameterData.parameterId &&
          p.isUserData !== updatedParameterData.isUserData &&
          p?.data !== updatedParameterData?.data &&
          ((updatedParameterData?.data && updatedParameterData.isUserData) ||
            (p?.data && !updatedParameterData.isUserData)),
      ),
    );
  });

  customCategorySelectedValueComputed = computed(() => {
    if (!this.parameterItems()?.length) return 0;
    return this.customCategorySelectedValue();
  });

  computedSelectedCategoryId = computed(() => {
    if (
      this.parameterItems()?.some(
        (parameterItem) => parameterItem.category.id === this.selectedCategoryId(),
      )
    ) {
      return this.selectedCategoryId();
    }
    return this.parameterItems()?.[0]?.category.id;
  });

  getUserAccessForParameter(parameter: dsDtos.Parameter) {
    return this.deviceTypeParameterConfigWithAvailableList()
      ?.filter((c) => c.parameterId === parameter.id)
      .map((c) => c.userAccess);
  }

  parameterMatchesUserAccess(parameter: dsDtos.Parameter, userAccessList: dsEnums.UserAccess[]) {
    if (!userAccessList) {
      return true;
    }
    const parameterUserAccess = this.getUserAccessForParameter(parameter);
    return parameterUserAccess?.every((userAccess) => userAccessList.includes(userAccess));
  }

  hasBasicParameters = computed(() => {
    const userAccess = this.userAccess();
    return this.parameters()?.some((parameter) => {
      const matchesAccess = this.parameterMatchesUserAccess(parameter, userAccess);
      const matchesLevel = parameter.advanced !== true;
      const matchesEnabledSubsystems = !this.disabledSubsystemIds()?.includes(
        parameter.subsystemId,
      );
      return matchesAccess && matchesLevel && matchesEnabledSubsystems;
    });
  });

  hasAdvancedParameters = computed(() => {
    const userAccess = this.userAccess();
    return this.parameters()?.some((parameter) => {
      const matchesAccess = this.parameterMatchesUserAccess(parameter, userAccess);
      const matchesLevel = parameter.advanced === true;
      const matchesEnabledSubsystems = !this.disabledSubsystemIds()?.includes(
        parameter.subsystemId,
      );
      return matchesAccess && matchesLevel && matchesEnabledSubsystems;
    });
  });

  shouldShowBasicOrAdvancedToggle = computed(
    () => this.showBasicOrAdvanced() && this.hasBasicParameters() && this.hasAdvancedParameters(),
  );

  private fallbackToAdvancedEffect = effect(() => {
    const basicOrAdvanced = this.basicOrAdvanced();
    const hasBasic = this.hasBasicParameters();
    const hasAdvanced = this.hasAdvancedParameters();
    if (basicOrAdvanced === 'basic' && !hasBasic && hasAdvanced) {
      this.basicOrAdvancedChange.emit('advanced');
    } else if (basicOrAdvanced === 'advanced' && !hasAdvanced && hasBasic) {
      this.basicOrAdvancedChange.emit('basic');
    }
  });

  filteredParameters = computed<dsDtos.Parameter[]>(() => {
    const userAccess = this.userAccess();
    const basicOrAdvanced = this.basicOrAdvanced();
    return this.parameters()?.filter((parameter) => {
      const matchesAccess = this.parameterMatchesUserAccess(parameter, userAccess);

      const matchesLevel = basicOrAdvanced === 'basic' ? parameter.advanced !== true : true;

      const matchesUserType = this.isParameterVisibleForUserType(parameter);

      return matchesAccess && matchesLevel && matchesUserType;
    });
  });

  /** combined parameters and parameterDataList in order to have them at the same obj*/
  parametersWithData = computed<ParameterWithData[]>(() => {
    const parameterData = this.hideChangedParameterData()
      ? this.parameterDataService.parameterDataList()
      : this.parameterDataService.updatedParameterDataList();
    const hasOverrides = (this.valueOverrideByParameterId()?.size ?? 0) > 0;
    return this.filteredParameters()
      ?.map<ParameterWithData>((parameter) => ({
        parameter,
        factory: parameterData?.find(
          ({ parameterId, isUserData }) => parameter.id === parameterId && !isUserData,
        ),
        user: parameterData?.find(
          ({ parameterId, isUserData }) => parameter.id === parameterId && isUserData,
        ),
      }))
      .filter(({ factory, user }) => hasOverrides || factory?.data || user || this.isEditable());
  });
  /** hierarchically parameters separated by category and subcategory*/
  parameterItems = computed<ParameterItem[]>(() =>
    this.categories()
      ?.map((category) => {
        //subcategories of current category
        const subCategories = category.subcategories ? [...category.subcategories] : [];
        subCategories?.push(undefined);
        return {
          category,
          /** combined subcategories with parameters and parameter data*/
          subCategoriesWithParameters: subCategories
            ?.map((subCategory) => ({
              id: category.id + subCategory,
              name: subCategory,
              parametersWithData: this.parametersWithData()
                ?.filter(
                  (parameterWithData) =>
                    parameterWithData.parameter?.subcategory === subCategory &&
                    parameterWithData.parameter?.categoryId === category.id,
                )
                .sort((a, b) => a.parameter.orderInSubcategory - b.parameter.orderInSubcategory),
            }))
            ?.filter((subCategory) => subCategory.parametersWithData?.length),
        };
      })
      ?.filter((category) => category.subCategoriesWithParameters?.length)
      .sort((a, b) => a.category.order - b.category.order),
  );

  ngOnInit() {
    this.init();
  }

  private expandAll() {
    this.parameterItems().forEach((parameter) => {
      this.selectedCategories.select(parameter?.category.id);
      parameter.subCategoriesWithParameters.forEach((subCategory) =>
        this.selectedSubCategories.select(subCategory.id),
      );
    });
  }

  private expandOnOverrideChange = effect(() => {
    this.valueOverrideByParameterId(); // tracked dependency
    if (this.shouldInitiallyExpanded()) {
      this.expandAll();
    }
  });

  init() {
    this.selectedCategoryId.set(undefined);
    this.customCategorySelectedValue.set(undefined);
    if (this.shouldInitiallyExpanded()) {
      this.expandAll();
    } else {
      this.selectedCategories.select(this.parameterItems()?.[0]?.category.id);
      this.selectedSubCategories.select(
        this.parameterItems()?.[0]?.subCategoriesWithParameters?.[0].id,
      );
    }
    this.selectedCategoryId.set(this.parameterItems()?.[0]?.category.id);
    if (!this.parameterItems()?.[0]?.category.id) {
      this.customCategorySelectedValue.set(this.categoryTitles()?.[0]?.value);
    }
  }

  selectedParameterItem = computed(() =>
    this.parameterItems()?.find(
      (parameterItem) => parameterItem.category.id === this.computedSelectedCategoryId(),
    ),
  );

  onClickCategory(id: string) {
    this.selectedCategories.toggle(id);
    this.selectedCategoryId.set(id);
    this.customCategorySelectedValue.set(undefined);
    this.changeCategory.emit(id);
  }

  isCategoryExpanded(id: string) {
    return this.selectedCategories.isSelected(id);
  }

  onClickSubCategory(id: string) {
    this.selectedSubCategories.toggle(id);
  }

  isSubCategoryExpanded(id: string) {
    return this.selectedSubCategories.isSelected(id);
  }

  onChangeParameterData($event: dsDtos.ParameterData) {
    this.parameterDataService.changedParameterDataList.update((preParameterDataList) => {
      const oldParameterData =
        this.parameterDataService
          .parameterDataList()
          ?.find(
            (parameterData) =>
              parameterData.parameterId === $event.parameterId &&
              parameterData.isUserData === $event.isUserData,
          )?.data ||
        this.parameterDataService
          .parameterDataList()
          ?.find((parameterData) => parameterData.parameterId === $event.parameterId)?.data;
      const newParameterDataList = preParameterDataList?.filter(
        (preParameterData) =>
          preParameterData.parameterId !== $event.parameterId ||
          preParameterData.isUserData !== $event.isUserData,
      );
      if (oldParameterData !== $event?.data && !(!oldParameterData && !$event?.data)) {
        newParameterDataList.push($event);
      }
      return newParameterDataList;
    });
  }

  onSubmitChanges() {
    this.submitParameter.emit(this.parameterDataService.changedParameterDataList());
  }

  onResetToFactory(id: string) {
    this.onChangeParameterData({ parameterId: id, data: undefined, isUserData: true });
  }

  onClickCustomCategory(value: CategoryTitleValue) {
    this.customCategorySelectedValue.set(value);
    this.changeCategory.emit(value);
  }

  onReload() {
    this.reload.emit();
    this.parameterDataService.changedParameterDataList.set([]);
    this.parametersEditState.set({});
  }

  parameterNameAndDescription(parameter: dsDtos.Parameter): string {
    if (!parameter?.name) return '';
    let hoverText = parameter.subcategory
      ? `${parameter.subcategory} ${parameter.name}`
      : parameter.name;
    if (parameter.description?.trim()) {
      hoverText += `:\n${parameter.description}`;
    }
    return hoverText;
  }

  isEdit(parameter: ParameterWithData) {
    return this.parametersEditState()?.[parameter.parameter.id];
  }

  onChangeEdit(parameter: ParameterWithData, $event: boolean) {
    this.parametersEditState.update((preValue) => ({
      ...(preValue || {}),
      [parameter.parameter.id]: $event,
    }));
  }

  getNumberOfUnsubmittedChanges(parameterItem: ParameterItem) {
    if (this.hideChangedParameterData()) return undefined;
    return (
      this.parameterDataService
        .changedParameterDataList()
        .filter((changedParameterData) =>
          parameterItem.subCategoriesWithParameters.some((subCategory) =>
            subCategory.parametersWithData.some(
              (parameterWithData) =>
                parameterWithData.parameter.id === changedParameterData.parameterId,
            ),
          ),
        ).length || undefined
    );
  }

  isParameterChanged(id: string) {
    if (this.hideChangedParameterData()) return false;
    return this.parameterDataService
      .changedParameterDataList()
      .some((changedParameterData) => id === changedParameterData.parameterId);
  }

  onParameterUndo($event: DeviceTypeParameterConfigWithAvailableListAndParameter) {
    this.parameterDataService.changedParameterDataList.update((preParameterDataList) =>
      preParameterDataList.filter(
        (preParameterData) => preParameterData.parameterId !== $event.parameter.id,
      ),
    );
  }

  showResetParameter(id: string) {
    if (!this.showReset() || this.isParameterChanged(id)) return false;
    const parameter = this.parametersWithData().find(
      (parameterData) => parameterData.parameter.id === id,
    );
    return parameter.user?.data !== undefined && parameter.user?.data !== parameter.factory?.data;
  }

  findFalseIndexes(array: boolean[]) {
    return array
      ?.map((element, index) => {
        if (!element) return index;
      })
      .filter((index) => index !== undefined);
  }

  onClear() {
    this.clear.emit();
  }

  onChangeBasicOrAdvanced($event: MatButtonToggleChange) {
    this.basicOrAdvancedChange.emit($event.value);
  }

  isParameterDiffering(id: string): boolean {
    return this.showMode() === 'all' && (this.differingParameterIds()?.has(id) ?? false);
  }

  getErrorByParameterId(id: string): string | undefined {
    return this.errorByParameterId()?.get(id);
  }

  getValueOverrideByParameterId(id: string): string | undefined {
    const map = this.valueOverrideByParameterId();
    if (!map?.size) return undefined;
    for (const innerMap of map.values()) {
      const val = innerMap.get(id);
      if (val !== undefined) return val;
    }
    return undefined;
  }

  getValueOverrideForCollar(columnTitle: string, parameterId: string): string | undefined {
    return this.valueOverrideByParameterId()?.get(columnTitle)?.get(parameterId);
  }

  formatOverrideValue(parameter: dsDtos.Parameter, data: string | undefined): string {
    if (data === undefined) return '';
    if (
      parameter.value === 'FLAGS' &&
      parameter.name === 'Visibility Flags' &&
      this.subsystems()?.length
    ) {
      return (
        this.subsystems()
          .filter((s) => this.parameterFormatService.isSubsystemVisible(s.identifier, data))
          .map((s) => s.name)
          .join(', ') || ''
      );
    }
    return data;
  }

  overrideTooltip(parameterId: string, fullValue: string | undefined): string {
    const warning = this.warningTooltipByParameterId()?.get(parameterId);
    if (warning) return warning;
    if (fullValue && fullValue.length > 50) return fullValue;
    return '';
  }

  isParameterDisabled(id: string): boolean {
    return this.disabledByParameterId()?.get(id) ?? false;
  }

  /**
   * Determines if a parameter should be shown based on isEditable, userOrFactory and isRemote:
   *
   * not editable: always true (visibility controlled by factory/user data presence)
   * allFactory: write
   * allUser:    write + isAvailableAsUserParameter + visibleSubsystem
   * customer (remote):  write + isAvailableAsUserParameter + WRITE_REMOTE + visibleSubsystem
   * customer (desktop): write + isAvailableAsUserParameter + (WRITE_REMOTE or WRITE_LOCAL) + visibleSubsystem
   */
  isParameterVisibleForUserType(parameter: dsDtos.Parameter): boolean {
    const userOrFactory = this.userOrFactory();
    const isVisibleSubsystem = !this.disabledSubsystemIds()?.includes(parameter.subsystemId);
    if (!userOrFactory || userOrFactory === 'all') {
      return isVisibleSubsystem;
    }

    if (userOrFactory === 'allFactory') {
      return parameter.write === true && !parameter.obsolete;
    }

    if (userOrFactory === 'allUser') {
      return (
        parameter.write === true &&
        parameter.isAvailableAsUserParameter === true &&
        isVisibleSubsystem &&
        !parameter.obsolete
      );
    }

    // customer
    const writeAccessValues: dsEnums.UserAccess[] = this.isRemote()
      ? ['WRITE_REMOTE']
      : ['WRITE_REMOTE', 'WRITE_LOCAL'];

    return (
      parameter.write === true &&
      parameter.isAvailableAsUserParameter === true &&
      isVisibleSubsystem &&
      this.parameterMatchesUserAccess(parameter, writeAccessValues)
    );
  }

  protected readonly Translator = Translator;
}
